import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import GeneralSmartInsightsRecommendationSummary from 'lumen/components/general/smart-insights/recommendation-summary';
import type {
  SmartInsightDay,
  SmartInsightDayInsightEntry,
} from 'lumen/models';

import styles from './card-smart-insights.module.css';

export interface MarketSmartInsightsCardSmartInsightsSignature {
  Args: {
    isGroupByStayDate: boolean;
    smartInsightDay: SmartInsightDay;
  };
  Element: HTMLDivElement;
}

export default class MarketSmartInsightsCardSmartInsights extends Component<MarketSmartInsightsCardSmartInsightsSignature> {
  @service declare portfolioSmartInsights: Services['portfolio-smart-insights'];

  get badgeText(): string {
    const { isGroupByStayDate, smartInsightDay } = this.args;

    if (isGroupByStayDate) {
      return smartInsightDay.hotelName;
    }

    return dayjs(smartInsightDay.stayDate).format('ddd, MMM D');
  }

  get firstInsightEntry(): SmartInsightDayInsightEntry | undefined {
    if (!this.sortedInsightEntries?.length) {
      return undefined;
    }
    return this.sortedInsightEntries[0];
  }

  get isExpanded(): boolean {
    return (
      this.portfolioSmartInsights.selectedSmartInsightDay ===
      this.args.smartInsightDay
    );
  }

  get remainingInsightEntryCount(): number {
    return Math.max(this.sortedInsightEntries.length - 1, 0);
  }

  get sortedInsightEntries(): SmartInsightDayInsightEntry[] {
    return this.args.smartInsightDay.insightEntriesSortedByRelevance;
  }

  <template>
    <div
      class="{{styles.card}}
        portfolio-smart-insights-card
        {{if this.isExpanded 'expanded'}}"
      ...attributes
    >
      <div class="flex flex-row items-center justify-between gap-200">
        <h4 class="max-w-[228px] truncate">
          {{this.badgeText}}
        </h4>

        <PrismIcon
          @color="intelligence"
          @name={{@smartInsightDay.priorityIcon}}
        />
      </div>

      <div class="border-neutral-default border-t"></div>

      {{#if this.firstInsightEntry}}
        <p class="text-300--regular max-w-[228px]">
          {{htmlSafe this.firstInsightEntry.formattedText}}
        </p>
      {{/if}}

      {{#if this.remainingInsightEntryCount}}
        <p
          class="text-200--regular text-neutral-subdued max-w-[228px] whitespace-nowrap"
        >
          {{t
            "market.smart_insights.count_more"
            count=this.remainingInsightEntryCount
          }}
        </p>
      {{/if}}

      {{#if @smartInsightDay.recommendationSummaryLabel}}
        <div class="border-neutral-default border-t"></div>

        <GeneralSmartInsightsRecommendationSummary
          @label={{@smartInsightDay.recommendationSummaryLabel}}
        />
      {{/if}}
    </div>
  </template>
}
